import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ToastContext } from '@/components/common/CommonToast';
import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import { GraduationColumns } from '@/pages/graduation-management/table/GraduationColumns';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';
import { GraduationType } from '@/types/graduation';
import { useLoading } from '@/components/common/loader/LoadingContext';
import DialogCustom from '@/components/common/ui/dialog';
import ButtonCustom from '@/components/common/ui/button';

type Props = {
  uploadCsv: string;
  searchTrigger: number;
};

const GraduationList: React.FC<Props> = ({ uploadCsv, searchTrigger }) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());
  const navigate = useNavigate();
  const { setLoadingGlobal } = useLoading();

  const [selectedGraduation, setSelectedGraduation] = useState<any>();
  const [dialogChangeStatusVisible, setDialogChangeStatusVisible] =
    useState(false);

  const { data: graduations, error } = useFetchClient(
    async () => await apiService.graduations.query(query, params),
    [query, uploadCsv, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE };
  }, [query]);

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    graduations?.pagination,
  );

  function handleEditGraduation(data: GraduationType) {
    const { id } = data;

    navigate(
      `${PATH.graduation}/${PATH.graduation_edit}/${id}`,
    );
  }

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  function handleError(error: any) {
    const { message } = error;

    const msg = message;
    toast?.current?.show(toastErrorMessage(msg));
  }

  async function handleEditStatusGraduation(
    isActive: boolean,
    rowData: GraduationType,
  ) {
    setSelectedGraduation({ isActive, rowData });

    const { id } = rowData;
    if (!id) {
      return;
    }

    const nextDialogVisibleValue = !dialogChangeStatusVisible;
    setDialogChangeStatusVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;

    setLoadingGlobal(true);
    const { data: graduation, error: graduationError } = await apiService.graduations.update(id, {
      ...rowData,
      is_visible: isActive,
    });
    setLoadingGlobal(false);
    if (graduationError) {
      handleError(graduationError);
      return;
    }

    const indexUpdate = graduations.data.findIndex((graduation: any) => graduation.id === id);
    if (indexUpdate !== -1) {
      graduations.data[indexUpdate].is_visible = graduation.data.is_visible;
    }

    const msg =
      graduation.data.is_visible
        ? t('graduation_list.toast.public_graduation_success')
        : t('graduation_list.toast.hide_graduation_success');
    toast?.current?.show(toastMessage(msg));
  }

  return (
    <>
      <CustomDataTable
        wrapClassName="mt-[20px]"
        values={graduations?.data ?? []}
        columns={GraduationColumns(t, handleEditGraduation, handleEditStatusGraduation)}
        pagination={paginationComputed}
        onPaginationChanged={onPaginationChanged}
        onSortChanged={onSortChanged}
      />

      <DialogCustom
        header={
          selectedGraduation?.isActive
            ? t('graduation_list.modal.change_status_public_to_draft.title')
            : t('graduation_list.modal.change_status_draft_to_public.title')
        }
        visible={dialogChangeStatusVisible}
        headerClassName="border-b border-gray-200 !py-[13px]"
        contentClassName="border-b border-gray-200 !p-[16px] !text-[16px] !leading-[24px] text-center text-gray-900"
        onHide={() => setDialogChangeStatusVisible(false)}
        footer={
          <div className="flex justify-end gap-4">
            <ButtonCustom
              type="button"
              className="!rounded-md"
              severity="secondary"
              onClick={() => setDialogChangeStatusVisible(false)}
            >
              {selectedGraduation?.isActive
                ? t('graduation_list.modal.change_status_public_to_draft.cancel')
                : t('graduation_list.modal.change_status_draft_to_public.cancel')}
            </ButtonCustom>
            <ButtonCustom
              type="button"
              className="!rounded-md"
              onClick={() =>
                handleEditStatusGraduation(
                  selectedGraduation.isActive,
                  selectedGraduation.rowData,
                )
              }
            >
              {selectedGraduation?.isActive
                ? t('graduation_list.modal.change_status_public_to_draft.yes')
                : t('graduation_list.modal.change_status_draft_to_public.yes')}
            </ButtonCustom>
          </div>
        }
      >
        {!selectedGraduation?.isActive
          ? t('graduation_list.modal.change_status_public_to_draft.content')
          : t('graduation_list.modal.change_status_draft_to_public.content')}
      </DialogCustom>
    </>
  );
};

export default GraduationList;
